import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Car, Inbox, Search, Clock, ShieldCheck, LogOut, Loader2, BarChart3, Trash2, Eye, EyeOff, CheckCircle2, Ban, Users } from "lucide-react";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AdminStats } from "@/components/admin/AdminStats";
import { AdminCommunity } from "@/components/admin/AdminCommunity";

const TABS = [
  { id: "stats", label: "Statistiques", icon: BarChart3 },
  { id: "community", label: "Communauté", icon: Users },
  { id: "agencies", label: "Agences", icon: Building2 },
  { id: "vehicles", label: "Véhicules", icon: Car },
  { id: "requests", label: "Demandes", icon: Inbox },
];

const STATUS_LABEL = { pending: "En attente", approved: "Validée", suspended: "Suspendue" };
const STATUS_STYLE = { pending: "bg-amber-50 text-amber-700", approved: "bg-emerald-50 text-emerald-700", suspended: "bg-red-50 text-red-700" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("all");

  const isAdmin = user && (user.role === "admin" || user.is_admin);

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && !isAdmin) navigate("/owner");
  }, [user, isAdmin, navigate]);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/agencies").then((r) => setAgencies(r.data));
    api.get("/admin/vehicles").then((r) => setVehicles(r.data));
    api.get("/admin/requests").then((r) => setRequests(r.data));
  }, []);

  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin, loadAll]);

  if (!isAdmin) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/agencies/${id}/status`, { status });
      toast.success(status === "approved" ? "Agence validée" : status === "suspended" ? "Agence suspendue" : "Agence remise en attente");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleVehicle = async (id) => {
    await api.patch(`/admin/vehicles/${id}/availability`);
    loadAll();
  };
  const deleteVehicle = async (id) => {
    if (!window.confirm("Supprimer ce véhicule ?")) return;
    await api.delete(`/admin/vehicles/${id}`);
    toast.success("Véhicule supprimé");
    loadAll();
  };

  const shownAgencies = filter === "all" ? agencies : agencies.filter((a) => a.status === filter);
  const kpis = stats ? [
    { label: "Agences", value: stats.agencies.total, icon: Building2, color: "#0B132B" },
    { label: "En attente", value: stats.agencies.pending, icon: Clock, color: "#D97706" },
    { label: "Véhicules", value: stats.vehicles.total, icon: Car, color: "#006233" },
    { label: "Recherches", value: stats.searches.total, icon: Search, color: "#C1272D" },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Administration</p>
            <h1 className="font-display text-3xl font-extrabold text-slate-900" data-testid="admin-title">Tableau de bord plateforme</h1>
            <p className="text-slate-500 text-sm mt-1">{user.email}</p>
          </div>
          <Button variant="outline" onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout"><LogOut className="h-4 w-4 mr-1.5" /> Déconnexion</Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {kpis.map((s) => (
            <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`admin-kpi-${s.label}`}>
              <s.icon className="h-5 w-5 mb-3" style={{ color: s.color }} />
              <p className="font-display text-3xl font-extrabold text-slate-900">{s.value}</p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-1 border-b border-slate-200 mb-8 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? "text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}
              style={tab === t.id ? { borderColor: "#006233" } : {}}>
              <t.icon className="h-4 w-4" /> {t.label}
              {t.id === "agencies" && stats?.agencies.pending > 0 && <span className="ml-1 rounded-full bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 font-bold" data-testid="admin-pending-badge">{stats.agencies.pending}</span>}
            </button>
          ))}
        </div>

        {tab === "stats" && (stats ? <AdminStats stats={stats} /> : <Loader2 className="h-6 w-6 animate-spin text-slate-400" />)}
        {tab === "community" && <AdminCommunity />}

        {tab === "agencies" && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex flex-wrap gap-2 p-4 border-b border-slate-100">
              {["all", "pending", "approved", "suspended"].map((f) => (
                <button key={f} data-testid={`admin-agency-filter-${f}`} onClick={() => setFilter(f)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === f ? "text-white" : "bg-slate-100 text-slate-600"}`}
                  style={filter === f ? { background: "#0B132B" } : {}}>
                  {f === "all" ? "Toutes" : STATUS_LABEL[f]}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="px-4 py-3">Agence</th><th className="px-4 py-3">Ville</th><th className="px-4 py-3">ICE / RC</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Véhicules</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {shownAgencies.map((a) => (
                    <tr key={a.id} className="border-t border-slate-100" data-testid={`admin-agency-row-${a.id}`}>
                      <td className="px-4 py-3"><p className="font-semibold text-slate-900">{a.agency_name}</p><p className="text-xs text-slate-500">{a.owner_name} · {a.email}</p></td>
                      <td className="px-4 py-3">{a.city}</td>
                      <td className="px-4 py-3 text-xs" data-testid={`admin-agency-ice-${a.id}`}>{a.ice || a.rc ? <>{a.ice && <p>ICE {a.ice}</p>}{a.rc && <p>RC {a.rc}</p>}</> : <span className="text-amber-600 font-medium">Non renseigné</span>}</td>
                      <td className="px-4 py-3 capitalize">{a.plan}</td>
                      <td className="px-4 py-3">{a.vehicle_count}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[a.status]}`} data-testid={`admin-agency-status-${a.id}`}>{STATUS_LABEL[a.status]}</span></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {a.status !== "approved" && <Button size="sm" className="text-white mr-2" style={{ background: "#006233" }} onClick={() => setStatus(a.id, "approved")} data-testid={`admin-approve-${a.id}`}><CheckCircle2 className="h-4 w-4 mr-1" /> Valider</Button>}
                        {a.status !== "suspended" && <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => setStatus(a.id, "suspended")} data-testid={`admin-suspend-${a.id}`}><Ban className="h-4 w-4 mr-1" /> Suspendre</Button>}
                      </td>
                    </tr>
                  ))}
                  {shownAgencies.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Aucune agence.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "vehicles" && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3">Véhicule</th><th className="px-4 py-3">Agence</th><th className="px-4 py-3">Ville</th><th className="px-4 py-3">Prix/jour</th><th className="px-4 py-3">Dispo</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.id} className="border-t border-slate-100" data-testid={`admin-vehicle-row-${v.id}`}>
                    <td className="px-4 py-3 flex items-center gap-3"><img src={v.image_url} alt="" className="h-10 w-14 rounded object-cover bg-slate-100" /><div><p className="font-semibold text-slate-900">{v.brand} {v.model}</p><p className="text-xs text-slate-500">{v.category} · {v.year}</p></div></td>
                    <td className="px-4 py-3">{v.agency?.agency_name}</td>
                    <td className="px-4 py-3">{v.city}</td>
                    <td className="px-4 py-3 font-semibold">{v.price_per_day} MAD</td>
                    <td className="px-4 py-3">{v.available ? <span className="text-emerald-700 text-xs font-semibold">Oui</span> : <span className="text-slate-400 text-xs font-semibold">Non</span>}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => toggleVehicle(v.id)} data-testid={`admin-vehicle-toggle-${v.id}`}>{v.available ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteVehicle(v.id)} data-testid={`admin-vehicle-delete-${v.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "requests" && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Véhicule</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Période</th><th className="px-4 py-3">Statut</th></tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{r.vehicle_label}</td>
                    <td className="px-4 py-3">{r.client_name}<p className="text-xs text-slate-500">{r.client_email}</p></td>
                    <td className="px-4 py-3">{r.start_date} → {r.end_date}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.status === "accepted" ? "bg-emerald-50 text-emerald-700" : r.status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{r.status}</span></td>
                  </tr>
                ))}
                {requests.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">Aucune demande.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
